import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import api from "../config/api";
import "./Contracts.css";
import "./Contracts-billing.css";
import { useContractFilters } from "../hooks/useContractFilters";
import { useChangesContext } from "../contexts/ChangesContext";
import { toast } from "react-toastify";
import {
  formatCodingType,
  formatCodingTypeAndTask,
  getContractFamily,
} from "../utils/codingUtils";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles, formSelectStyles } from "../utils/selectStyles";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";
import { syncOutcomes } from "../utils/syncSummary";
import { errorMessage } from "../utils/errors";
import { syncEntity } from "../utils/syncEntity";

/**
 * Two features nobody currently uses, hidden rather than deleted.
 *
 * "Facturer des contrats" marks contracts as facturé or payé in bulk, and the kanban
 * is a second reading of the same list. Both work; they are simply not part of how the
 * page is used today, and the row of buttons carrying them was the first thing on the
 * page. Set either back to `true` to bring it back — the handlers, the modal and the
 * kanban rendering are all still here and still wired up.
 *
 * The switches are read inside the JSX rather than around it, so the code they guard
 * stays referenced and cannot rot unnoticed: a rename or a broken type still fails the
 * build.
 */
const SHOW_BILLING = false;
const SHOW_KANBAN = false;

/**
 * Contracts sent out and not settled yet, whether or not the signer has opened them.
 *
 * `viewed` reaches us from the signature service the moment the document is opened, and
 * says nothing about the signature itself — such a contract is still awaiting one, and
 * offers the same actions as one merely sent.
 */
const AWAITING_SIGNATURE_STATUSES = ["sent_for_signature", "viewed"];

interface Contract {
  id: number;
  status: string; // 'non facturé', 'facturé', 'payé'
  contractStatus: string; // Based on signature: 'pending', 'no_contract', 'unsigned', 'signed', 'sent_for_signature', 'viewed', 'declined', 'cancelled'
  createdAt?: string; // Date de création du contrat
  factureId?: number;
  facture?: any;
  codages: Coding[];
  supervisions: Supervision[];
  cancellationReason?: string;
  cancelledAt?: string;
}

interface Coding {
  id: number;
  aia_codage_Date: string;
  aia_codage_type: string;
  aia_codage_task_type: string | null;
  aia_game_name: string;
  coder: {
    id?: number;
    first_name: string;
    last_name: string;
    enterprise?: {
      nom: string;
      sirene?: string;
      adresse?: string;
      mail?: string;
    };
  };
  game: {
    gameName: string;
    metadata?: {
      match_date?: string;
    };
  };
  teamName?: string;
}

interface Supervision {
  id: number;
  aia_supervision_date: string;
  aia_supervision_type: string;
  aia_game_name: string;
  supervisor: {
    id?: number;
    first_name: string;
    last_name: string;
    enterprise?: {
      nom: string;
      sirene?: string;
      adresse?: string;
      mail?: string;
    };
  };
  coder: {
    id?: number;
    first_name: string;
    last_name: string;
  };
  game: {
    gameName: string;
    metadata?: {
      match_date?: string;
    };
  };
}

const Contracts: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const [selectedContracts, setSelectedContracts] = useState<Set<number>>(
    new Set(),
  );
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelContractId, setCancelContractId] = useState<number | null>(null);
  const [cancellationReason, setCancellationReason] = useState("");
  const [isBulkCancel, setIsBulkCancel] = useState(false);

  // Billing modal states
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [selectedEnterpriseForBilling, setSelectedEnterpriseForBilling] =
    useState<any>(null);
  const [unbilledContracts, setUnbilledContracts] = useState<any[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<Set<number>>(
    new Set(),
  );
  const [contractsByType, setContractsByType] = useState({
    Individual: { total: 0, selected: 0 },
    Collective: { total: 0, selected: 0 },
    Live: { total: 0, selected: 0 },
  });
  const [billingFilters, setBillingFilters] = useState({
    dateStart: "",
    dateEnd: "",
    status: "",
  });
  const [statusToApply, setStatusToApply] = useState<"facturé" | "payé">(
    "facturé",
  );
  const [syncingCodings, setSyncingCodings] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sorting states
  const [dateSortOrder, setDateSortOrder] = useState<"asc" | "desc">("desc");

  // Pending contracts states
  const [selectedPendingContracts, setSelectedPendingContracts] = useState<
    Set<number>
  >(new Set());
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false);
  const [sendingContracts, setSendingContracts] = useState(false);
  // const [isPendingCollapsed, setIsPendingCollapsed] = useState(true); // Only used in commented section

  // Ref to track initial mount
  const isInitialMount = useRef(true);

  // Helper function to get contract status label
  const getContractStatusLabel = (status: string): string => {
    switch (status) {
      case "pending":
        return "À traiter";
      case "no_contract":
        return "Sans Contrat";
      case "unsigned":
        return "Non Signé";
      case "sent_for_signature":
        return "En attente";
      case "viewed":
        return "Consulté";
      case "signed":
        return "Signé";
      case "declined":
        return "Refusé";
      case "cancelled":
        return "Annulé";
      default:
        return status || "Non Signé";
    }
  };

  // A codings sync with detection records coder changes it refuses to apply, so the
  // Modifications badge has to be recounted as soon as it finishes.
  const { triggerRefresh } = useChangesContext();

  // `?contractId=` opens the page on a single contract — how the Codings page links a
  // coding to its contract. Read once, as the initial filter state, so the user can
  // then widen the filters without the URL pulling them back.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialContractId = useMemo(
    () => searchParams.get("contractId") || undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // `?contractStatus=` opens the page on the statuses a dashboard figure counted, so
  // the number clicked there and the rows shown here describe the same set. Read once,
  // like the id above.
  const initialContractStatus = useMemo(
    () =>
      (searchParams.get("contractStatus") || "")
        .split(",")
        .map((status) => status.trim())
        .filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Use the modern filter hook
  const {
    filters,
    filterOptions,
    updateFilter,
    updateContractIdFilter,
    resetFilters,
    hasActiveFilters,
  } = useContractFilters(contracts, initialContractId, initialContractStatus);

  // Store filters in ref to avoid recreating fetchContracts
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const fetchContracts = useCallback(async (page: number, limit: number) => {
    try {
      // Build query params with filters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      const currentFilters = filtersRef.current;
      if (currentFilters.search) params.append("search", currentFilters.search);
      if (currentFilters.contractStatus.length > 0)
        params.append(
          "contractStatus",
          currentFilters.contractStatus.join(","),
        );
      if (currentFilters.paymentStatus.length > 0)
        params.append("paymentStatus", currentFilters.paymentStatus.join(","));
      if (currentFilters.dateStart)
        params.append("dateStart", currentFilters.dateStart);
      if (currentFilters.dateEnd)
        params.append("dateEnd", currentFilters.dateEnd);
      if (currentFilters.contractId)
        params.append("contractId", currentFilters.contractId);

      const response = await api.get(`/api/contracts?${params.toString()}`);
      const { data, pagination } = response.data;

      setContracts(data);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
      setCurrentPage(pagination.page);
    } catch (error) {
      console.error("Error fetching contracts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts(currentPage, pageSize);
    loadEnterprises();
    // Auto-refresh every 10 seconds to get updated contract statuses from polling
    const interval = setInterval(
      () => fetchContracts(currentPage, pageSize),
      10000,
    );
    return () => clearInterval(interval);
  }, [currentPage, pageSize, fetchContracts]);

  // Debounce filter changes and reset to page 1
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchContracts(1, pageSize);
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [
    filters.search,
    filters.contractStatus,
    filters.paymentStatus,
    filters.dateStart,
    filters.dateEnd,
    filters.contractId,
    fetchContracts,
    pageSize,
  ]);

  /**
   * Clears the filters, including the `?contractId=` the page may have been opened
   * with — the URL is dropped too, so a reload does not reapply it.
   */
  const handleResetFilters = () => {
    resetFilters();
    if (searchParams.has("contractId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("contractId");
      setSearchParams(next, { replace: true });
    }
  };

  const handleViewContract = async (contract: Contract) => {
    try {
      // TODO: Call API to download contract
      console.log("Downloading contract for:", contract.id);
      // const response = await api.get(`/api/contracts/${contract.id}/download`);
      // Handle file download
    } catch (error) {
      console.error("Error downloading contract:", error);
    }
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Pending contracts handlers
  const pendingContracts = contracts.filter(
    (c) => c.contractStatus === "pending",
  );

  // Sort contracts by date
  const sortContractsByDate = (contractsToSort: Contract[]) => {
    return [...contractsToSort].sort((a, b) => {
      // Get the first coding's date for each contract
      const dateA = a.codages[0]?.game?.metadata?.match_date;
      const dateB = b.codages[0]?.game?.metadata?.match_date;

      // Handle cases where date is missing
      if (!dateA && !dateB) return 0;
      if (!dateA) return 1; // Put contracts without date at the end
      if (!dateB) return -1;

      const timeA = new Date(dateA).getTime();
      const timeB = new Date(dateB).getTime();

      return dateSortOrder === "desc" ? timeB - timeA : timeA - timeB;
    });
  };

  // Apply sorting to contracts
  const sortedContracts = sortContractsByDate(contracts);

  // Toggle date sort order
  const toggleDateSort = () => {
    setDateSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  // Calculate how many selected contracts are pending
  const selectedPendingFromBulk = sortedContracts.filter(
    (c) => selectedContracts.has(c.id) && c.contractStatus === "pending",
  );
  const selectedPendingCount = selectedPendingFromBulk.length;

  // Calculate how many selected contracts are NOT pending (can be factured/paid)
  const selectedNonPendingCount = sortedContracts.filter(
    (c) => selectedContracts.has(c.id) && c.contractStatus !== "pending",
  ).length;

  /* COMMENTED OUT - handleSelectPendingContract - Used only in commented section
  const handleSelectPendingContract = (
    contractId: number,
    checked: boolean,
  ) => {
    // Validate contract ID
    if (typeof contractId !== "number" || isNaN(contractId)) {
      console.error("Invalid contract ID:", contractId);
      return;
    }

    const newSelection = new Set(selectedPendingContracts);
    if (checked) {
      newSelection.add(contractId);
    } else {
      newSelection.delete(contractId);
    }
    setSelectedPendingContracts(newSelection);
  };
  */

  /* COMMENTED OUT - handleSelectAllPending - Used only in commented section
  const handleSelectAllPending = (checked: boolean) => {
    if (checked) {
      const validIds = pendingContracts
        .filter((c) => typeof c.id === "number" && !isNaN(c.id))
        .map((c) => c.id);
      setSelectedPendingContracts(new Set(validIds));
    } else {
      setSelectedPendingContracts(new Set());
    }
  };
  */

  /* COMMENTED OUT - handleOpenSendConfirmModal
  const handleOpenSendConfirmModal = () => {
    if (selectedPendingContracts.size === 0) {
      toast.warning("Veuillez sélectionner au moins un contrat");
      return;
    }
    setShowSendConfirmModal(true);
  };
  */

  const handleSendPendingContracts = async () => {
    try {
      setSendingContracts(true);
      const contractIds = Array.from(selectedPendingContracts);

      const response = await api.post("/api/contracts/send-contracts", {
        contractIds,
      });

      if (response.data.success) {
        const { results } = response.data;

        if (results.success.length > 0) {
          toast.success(
            `${results.success.length} contrat(s) envoyé(s) avec succès pour signature`,
          );
        }

        if (results.failed.length > 0) {
          toast.error(
            `${results.failed.length} contrat(s) n'ont pas pu être envoyés`,
          );
          console.error("Contrats en échec:", results.failed);
        }

        // Rafraîchir la liste des contrats
        await fetchContracts(currentPage, pageSize);

        // Réinitialiser la sélection
        setSelectedPendingContracts(new Set());
      }
    } catch (error: any) {
      console.error("Erreur envoi contrats:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de l'envoi des contrats",
      );
    } finally {
      setSendingContracts(false);
      setShowSendConfirmModal(false);
    }
  };

  const handleSendBulkPendingContracts = async () => {
    try {
      setSendingContracts(true);
      const pendingContractsToSend = contracts.filter(
        (c) => selectedContracts.has(c.id) && c.contractStatus === "pending",
      );
      const contractIds = pendingContractsToSend.map((c) => c.id);

      if (contractIds.length === 0) {
        toast.warning("Aucun contrat à traiter sélectionné");
        return;
      }

      const response = await api.post("/api/contracts/send-contracts", {
        contractIds,
      });

      if (response.data.success) {
        const { results } = response.data;

        if (results.success.length > 0) {
          toast.success(
            `${results.success.length} contrat(s) envoyé(s) avec succès pour signature`,
          );
        }

        if (results.failed.length > 0) {
          toast.error(
            `${results.failed.length} contrat(s) n'ont pas pu être envoyés`,
          );
          console.error("Contrats en échec:", results.failed);
        }

        // Rafraîchir la liste des contrats
        await fetchContracts(currentPage, pageSize);

        // Réinitialiser la sélection
        setSelectedContracts(new Set());
      }
    } catch (error: any) {
      console.error("Erreur envoi contrats:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de l'envoi des contrats",
      );
    } finally {
      setSendingContracts(false);
    }
  };

  const handleOpenBulkPendingCancelModal = () => {
    const pendingContractsToCancel = contracts.filter(
      (c) => selectedContracts.has(c.id) && c.contractStatus === "pending",
    );

    if (pendingContractsToCancel.length === 0) {
      toast.warning("Aucun contrat à traiter sélectionné");
      return;
    }

    setIsBulkCancel(true);
    setCancellationReason("");
    setShowCancelModal(true);
  };

  const handleSendSingleContract = async (contractId: number) => {
    if (typeof contractId !== "number" || isNaN(contractId)) {
      toast.error("ID de contrat invalide");
      return;
    }

    try {
      const response = await api.post("/api/contracts/send-contracts", {
        contractIds: [contractId],
      });

      if (response.data.success) {
        const { results } = response.data;

        // Pour un envoi unique, vérifier si CE contrat a réussi ou échoué
        if (results.success.includes(contractId)) {
          toast.success("Contrat envoyé avec succès pour signature");
          // Rafraîchir la liste des contrats
          await fetchContracts(currentPage, pageSize);
        } else if (results.failed.length > 0) {
          const failure = results.failed.find((f: any) => f.id === contractId);
          toast.error(failure?.reason || "Erreur lors de l'envoi du contrat");
        }
      }
    } catch (error: any) {
      console.error("Erreur envoi contrat:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de l'envoi du contrat",
      );
    }
  };

  const handleResendContract = async (contractId: number) => {
    if (typeof contractId !== "number" || isNaN(contractId)) {
      toast.error("ID de contrat invalide");
      return;
    }

    if (
      !window.confirm(
        "Êtes-vous sûr de vouloir renvoyer ce contrat ?\n\n" +
          "Cette action va :\n" +
          "1. Annuler la demande de signature actuelle\n" +
          "2. Créer une nouvelle demande de signature avec les informations actualisées de l'entreprise\n\n" +
          "L'entreprise recevra un nouvel email.",
      )
    ) {
      return;
    }

    try {
      // Appeler la route de renvoi - le backend récupère toutes les données actualisées
      const response = await api.post(
        `/api/contracts/${contractId}/resend-for-signature`,
      );

      if (response.data.success) {
        toast.success(
          "✅ Contrat renvoyé avec succès - Nouvelle demande de signature créée avec les données actualisées",
        );
        // Rafraîchir la liste des contrats
        await fetchContracts(currentPage, pageSize);
      }
    } catch (error: any) {
      console.error("Erreur renvoi contrat:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors du renvoi du contrat",
      );
    }
  };

  /* COMMENTED OUT - handleCancelPendingContracts
  const handleCancelPendingContracts = () => {
    if (selectedPendingContracts.size === 0) {
      toast.warning("Veuillez sélectionner au moins un contrat");
      return;
    }

    const validIds = Array.from(selectedPendingContracts).filter(
      (id) => typeof id === "number" && !isNaN(id),
    );
    setSelectedContracts(new Set(validIds));
    setIsBulkCancel(true);
    setCancellationReason("");
    setShowCancelModal(true);
  };
  */

  const handleToggleFactureStatus = async (contract: Contract) => {
    try {
      // Cycle: non facturé → facturé → payé → non facturé
      let newStatus: string;
      if (contract.status === "non facturé" || !contract.status) {
        newStatus = "facturé";
      } else if (contract.status === "facturé") {
        newStatus = "payé";
      } else {
        newStatus = "non facturé";
      }

      await api.patch(`/api/contracts/${contract.id}`, {
        status: newStatus,
      });

      toast.success(`Contrat marqué comme ${newStatus}`);
      fetchContracts(currentPage, pageSize);
    } catch (error) {
      console.error("Error toggling facture status:", error);
      toast.error("Erreur lors de la mise à jour du statut de facturation");
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const validIds = contracts
        .filter((c) => typeof c.id === "number" && !isNaN(c.id))
        .map((c) => c.id);
      setSelectedContracts(new Set(validIds));
    } else {
      setSelectedContracts(new Set());
    }
  };

  const handleSelectContract = (contractId: number, checked: boolean) => {
    if (typeof contractId !== "number" || isNaN(contractId)) {
      return;
    }

    const newSelected = new Set(selectedContracts);
    if (checked) {
      newSelected.add(contractId);
    } else {
      newSelected.delete(contractId);
    }
    setSelectedContracts(newSelected);
  };

  const handleBulkUpdateStatus = async (newStatus: string) => {
    try {
      const contractIds = Array.from(selectedContracts);

      // Utiliser le bulk endpoint pour une meilleure validation
      await api.post("/api/contracts/bulk-update-status", {
        contractIds: contractIds,
        status: newStatus,
      });

      toast.success(
        `${contractIds.length} contrat${
          contractIds.length > 1 ? "s" : ""
        } marqué${contractIds.length > 1 ? "s" : ""} comme ${newStatus}`,
      );

      setSelectedContracts(new Set());
      fetchContracts(currentPage, pageSize);
    } catch (error: any) {
      console.error("Error bulk updating contracts:", error);

      // 🚨 Gestion des erreurs de validation
      if (error.response?.status === 400 && error.response?.data?.conflicts) {
        const conflicts = error.response.data.conflicts;
        const errorMessage =
          error.response.data.message || error.response.data.error;

        // Créer un message détaillé avec tous les conflits
        let detailedMessage = `❌ ${errorMessage}\n\n`;
        detailedMessage += "Conflits détectés :\n\n";

        conflicts.forEach((conflict: any, index: number) => {
          detailedMessage += `${index + 1}. Contrat #${conflict.contractId}:\n`;
          detailedMessage += `   ${conflict.reason}\n`;
          if (conflict.currentStatus) {
            detailedMessage += `   Statut actuel: ${conflict.currentStatus}\n`;
          }
          if (conflict.paidAt) {
            detailedMessage += `   Payé le: ${new Date(
              conflict.paidAt,
            ).toLocaleDateString()}\n`;
          }
          detailedMessage += `\n`;
        });

        // Afficher l'alerte avec les détails
        toast.error(detailedMessage, {
          autoClose: false,
          style: {
            whiteSpace: "pre-line",
            maxWidth: "600px",
          },
        });

        console.error("Validation conflicts:", conflicts);
      } else {
        toast.error(
          error.response?.data?.error ||
            "Erreur lors de la mise à jour groupée",
        );
      }
    }
  };

  const openCancelModal = (contractId: number) => {
    if (typeof contractId !== "number" || isNaN(contractId)) {
      toast.error("ID de contrat invalide");
      return;
    }

    setCancelContractId(contractId);
    setIsBulkCancel(false);
    setCancellationReason("");
    setShowCancelModal(true);
  };

  const openBulkCancelModal = () => {
    if (selectedContracts.size === 0) {
      toast.warning("Veuillez sélectionner au moins un contrat");
      return;
    }
    setIsBulkCancel(true);
    setCancellationReason("");
    setShowCancelModal(true);
  };

  // Billing modal functions
  const loadEnterprises = async () => {
    try {
      // The endpoint answers { data, pagination } and paginates at 50 by default;
      // this dropdown needs every enterprise, so ask for a single large page.
      // Older deployments still answer with a bare array, hence the shape check.
      const response = await api.get("/api/enterprises?limit=1000");
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? [];
      setEnterprises(list);
    } catch (error) {
      console.error("Error loading enterprises:", error);
    }
  };

  const handleOpenBillingModal = () => {
    setShowBillingModal(true);
    setSelectedEnterpriseForBilling(null);
    setUnbilledContracts([]);
    setSelectedContractIds(new Set());
    setContractsByType({
      Individual: { total: 0, selected: 0 },
      Collective: { total: 0, selected: 0 },
      Live: { total: 0, selected: 0 },
    });
    setBillingFilters({
      dateStart: "",
      dateEnd: "",
      status: "",
    });
    setStatusToApply("facturé");
  };

  const handleEnterpriseSelectForBilling = async (enterprise: any) => {
    setSelectedEnterpriseForBilling(enterprise);

    if (!enterprise) {
      setUnbilledContracts([]);
      setContractsByType({
        Individual: { total: 0, selected: 0 },
        Collective: { total: 0, selected: 0 },
        Live: { total: 0, selected: 0 },
      });
      return;
    }

    try {
      const response = await api.get(
        `/api/contracts/unbilled/${enterprise.id}`,
      );
      const contracts = response.data;
      setUnbilledContracts(contracts);

      const counts = {
        Individual: { total: 0, selected: 0 },
        Collective: { total: 0, selected: 0 },
        Live: { total: 0, selected: 0 },
      };

      contracts.forEach((contract: any) => {
        const codingType = contract.codages?.[0]?.aia_codage_type || "Unknown";
        const isBilled = contract.status === "facturé";
        const isPaid = contract.status === "payé";

        // Individual_v2 counts as individual, see getContractFamily
        const family = getContractFamily(codingType);
        if (family === "individual") {
          counts.Individual.total++;
          if (isBilled || isPaid) counts.Individual.selected++;
        } else if (family === "collective") {
          counts.Collective.total++;
          if (isBilled || isPaid) counts.Collective.selected++;
        } else if (family === "collective_live") {
          counts.Live.total++;
          if (isBilled || isPaid) counts.Live.selected++;
        }
      });

      setContractsByType(counts);
    } catch (error) {
      console.error("Error loading unbilled contracts:", error);
      toast.error("Erreur lors du chargement des contrats non facturés");
    }
  };

  const handleToggleContractSelection = (
    contractId: number,
    codingType: string,
  ) => {
    const newSelected = new Set(selectedContractIds);
    const isCurrentlySelected = newSelected.has(contractId);

    if (isCurrentlySelected) {
      newSelected.delete(contractId);
    } else {
      newSelected.add(contractId);
    }

    setSelectedContractIds(newSelected);
  };

  const handleValidateBilling = async () => {
    if (selectedContractIds.size === 0) {
      toast.warning("Veuillez sélectionner au moins un contrat");
      return;
    }

    try {
      await api.post("/api/contracts/bulk-update-status", {
        contractIds: Array.from(selectedContractIds),
        status: statusToApply,
      });

      toast.success(
        `${selectedContractIds.size} contrat(s) marqué(s) comme ${statusToApply}`,
      );

      // Rafraîchir la liste des contrats de l'entreprise
      if (selectedEnterpriseForBilling) {
        handleEnterpriseSelectForBilling(selectedEnterpriseForBilling);
      }

      setSelectedContractIds(new Set());
    } catch (error: any) {
      console.error("Error updating contracts:", error);

      // 🚨 Gestion des erreurs de validation
      if (error.response?.status === 400 && error.response?.data?.conflicts) {
        const conflicts = error.response.data.conflicts;
        const errorMessage =
          error.response.data.message || error.response.data.error;

        // Créer un message détaillé avec tous les conflits
        let detailedMessage = `❌ ${errorMessage}\n\n`;
        detailedMessage += "Conflits détectés :\n\n";

        conflicts.forEach((conflict: any, index: number) => {
          detailedMessage += `${index + 1}. Contrat #${conflict.contractId}:\n`;
          detailedMessage += `   ${conflict.reason}\n`;
          if (conflict.currentStatus) {
            detailedMessage += `   Statut actuel: ${conflict.currentStatus}\n`;
          }
          if (conflict.paidAt) {
            detailedMessage += `   Payé le: ${new Date(
              conflict.paidAt,
            ).toLocaleDateString()}\n`;
          }
          detailedMessage += `\n`;
        });

        // Afficher l'alerte avec les détails
        toast.error(detailedMessage, {
          autoClose: false, // Ne pas fermer automatiquement
          style: {
            whiteSpace: "pre-line", // Respecter les sauts de ligne
            maxWidth: "600px",
          },
        });

        // Aussi afficher dans la console pour debug
        console.error("Validation conflicts:", conflicts);
      } else {
        // Erreur générique
        toast.error(
          error.response?.data?.error ||
            "Erreur lors de la mise à jour des contrats",
        );
      }
    }
  };

  const getFilteredBillingContracts = () => {
    let filtered = [...unbilledContracts];

    if (billingFilters.status) {
      filtered = filtered.filter(
        (contract) => contract.status === billingFilters.status,
      );
    }

    if (billingFilters.dateStart || billingFilters.dateEnd) {
      filtered = filtered.filter((contract) => {
        const contractDate = contract.codages?.[0]?.aia_codage_Date;
        if (!contractDate) return false;

        const date = new Date(contractDate);
        const startDate = billingFilters.dateStart
          ? new Date(billingFilters.dateStart)
          : null;
        const endDate = billingFilters.dateEnd
          ? new Date(billingFilters.dateEnd)
          : null;

        if (startDate && date < startDate) return false;
        if (endDate && date > endDate) return false;

        return true;
      });
    }

    return filtered;
  };

  const handleCancelContract = async () => {
    if (!cancellationReason.trim()) {
      toast.warning("Le motif d'annulation est obligatoire");
      return;
    }

    try {
      if (isBulkCancel) {
        // Bulk cancel

        const contractIds = Array.from(selectedContracts).filter(
          (id) => typeof id === "number" && !isNaN(id),
        );

        if (contractIds.length === 0) {
          toast.error("Aucun contrat valide sélectionné");
          return;
        }

        const response = await api.post("/api/contracts/bulk/cancel", {
          contractIds,
          cancellationReason: cancellationReason.trim(),
        });

        // Check for signature recall results
        if (response.data.recallResults) {
          const recallResults = response.data.recallResults;
          const recalled = recallResults.filter(
            (r: any) => r.status === "recalled",
          ).length;
          const failed = recallResults.filter(
            (r: any) => r.status === "recall_failed",
          ).length;

          if (recalled > 0) {
            toast.info(`${recalled} demande(s) de signature rappelée(s)`);
          }
          if (failed > 0) {
            toast.warning(
              `${failed} demande(s) n'ont pas pu être rappelées (peut-être déjà signées)`,
            );
          }
        }

        toast.success(
          `${contractIds.length} contrat${
            contractIds.length > 1 ? "s" : ""
          } annulé${contractIds.length > 1 ? "s" : ""} avec succès`,
        );
        setSelectedContracts(new Set());
        setSelectedPendingContracts(new Set());
      } else {
        // Single cancel
        const response = await api.post(
          `/api/contracts/${cancelContractId}/cancel`,
          {
            cancellationReason: cancellationReason.trim(),
          },
        );

        // Check for signature recall status
        if (response.data.recallStatus) {
          if (response.data.recallStatus === "recalled") {
            toast.info("Demande de signature rappelée");
          } else if (response.data.recallStatus === "recall_failed") {
            toast.warning(
              `Rappel échoué: ${
                response.data.recallError || "Peut-être déjà signé ou expiré"
              }`,
            );
          }
        }

        toast.success("Contrat annulé avec succès");
      }

      setShowCancelModal(false);
      setCancellationReason("");
      setCancelContractId(null);
      fetchContracts(currentPage, pageSize);
    } catch (error: any) {
      console.error("Error cancelling contract:", error);
      toast.error(
        error.response?.data?.error || "Erreur lors de l'annulation du contrat",
      );
    }
  };

  const closeCancelModal = () => {
    setShowCancelModal(false);
    setCancellationReason("");
    setCancelContractId(null);
    setIsBulkCancel(false);
    setSelectedContracts(new Set());
    setSelectedPendingContracts(new Set());
  };

  /**
   * Synchronises everything a contract is built from, in order: coders, matchs, then
   * the codings — that last step is what creates and updates the contracts.
   *
   * Sequential and in that order because each step reads what the previous one wrote:
   * a coding hangs off a match and off a coder, and a contract is generated from a
   * coding together with its coder's enterprise. A failure stops the run rather than
   * generating contracts from a half-imported set, and the steps that never ran are
   * named so an aborted run does not read as a complete one.
   */
  const handleSyncContracts = async () => {
    try {
      setSyncingCodings(true);
      toast.info("Synchronisation en cours...", {
        autoClose: false,
        toastId: "sync-codings",
      });

      const steps = ["Codeurs", "Matchs", "Contrats"];

      /** Names the steps left unrun, so an aborted run cannot pass for a finished one. */
      const announceAbandoned = (index: number) => {
        const skipped = steps.slice(index + 1);
        if (skipped.length) {
          toast.warning(`${skipped.join(" et ")} : synchronisation abandonnée`);
        }
      };

      // The two steps the codings depend on. Reported generically — there is no detail
      // in them worth more than what was created, updated or deleted.
      const preludes = [
        { label: "Codeurs", entity: "coders" as const },
        { label: "Matchs", entity: "games" as const },
      ];

      for (let index = 0; index < preludes.length; index++) {
        const { label, entity } = preludes[index];
        const data = await syncEntity(entity);

        if (!data.success) {
          toast.dismiss("sync-codings");
          toast.error(
            `${label} : ${data.message ?? "échec de la synchronisation"}`,
          );
          announceAbandoned(index);
          return;
        }

        const outcomes = syncOutcomes(data);
        toast.success(
          `${label} : ${outcomes.length ? outcomes.join(", ") : "aucun changement"}`,
        );
      }

      // The codings: the step that generates the contracts, and the one that detects
      // the coder changes the sync must not apply by itself.
      const codings = await syncEntity("codings");

      toast.dismiss("sync-codings");

      if (codings.success) {
        const syncStats = codings.stats as any;
        const changeDetection = codings.change_detection as any;
        const codingRequestsGeneration =
          codings.coding_requests_generation as any;

        const messageParts = [];

        // Stats de synchronisation - seulement si des créations
        if (syncStats && syncStats.created > 0) {
          messageParts.push(`${syncStats.created} nouveau(x) codage(s)`);
        }

        // Changements de codeur détectés
        if (changeDetection && changeDetection.changes_detected > 0) {
          messageParts.push(
            `${changeDetection.changes_detected} changement(s) de codeur`,
          );
        }

        // Contrats générés automatiquement - seulement si vraiment créés
        if (
          codingRequestsGeneration &&
          codingRequestsGeneration.contractsCreated > 0
        ) {
          messageParts.push(
            `${codingRequestsGeneration.contractsCreated} contrat(s) créé(s)`,
          );
        }

        // Message final
        let message = "Contrats : synchronisation terminée";
        if (messageParts.length > 0) {
          message += " : " + messageParts.join(", ");
        }

        toast.success(message, {
          autoClose: messageParts.length > 0 ? 8000 : 3000,
        });

        triggerRefresh();

        // Rafraîchir la liste des contrats
        fetchContracts(currentPage, pageSize);
      } else {
        toast.error("Contrats : échec de la synchronisation");
      }
    } catch (error: any) {
      toast.dismiss("sync-codings");
      console.error("Error syncing contracts:", error);
      toast.error(`Erreur: ${errorMessage(error)}`, {
        autoClose: 10000,
      });
    } finally {
      setSyncingCodings(false);
    }
  };

  // All filtering logic is now handled by the useContractFilters hook

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des contrats...</p>
      </div>
    );
  }

  return (
    <div className="contracts-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Contrats</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncingCodings ? "syncing" : ""}`}
                onClick={handleSyncContracts}
                disabled={syncingCodings}
                title="Synchronise les codeurs, les matchs puis les codages, ce qui met à jour les contrats"
              >
                {syncingCodings
                  ? "Synchronisation..."
                  : "Synchroniser contrats"}
              </button>
            </div>
          </div>
        </div>

        {/* Pending Contracts Section - COMMENTED OUT TEMPORARILY */}
        {/* {pendingContracts.length > 0 && (
          <div className="pending-contracts-section">
            <div className="pending-contracts-header">
              <div className="pending-header-content">
                <h2 className="section-title">
                  Contrats à traiter ({pendingContracts.length})
                </h2>
                <p className="section-description">
                  Ces contrats ont été créés automatiquement et sont en attente
                  d'envoi pour signature.
                </p>
              </div>
              <button
                className="collapse-toggle-btn"
                onClick={() => setIsPendingCollapsed(!isPendingCollapsed)}
                title={isPendingCollapsed ? "Déplier" : "Replier"}
              >
                {isPendingCollapsed ? "▼" : "▲"}
              </button>
            </div>

            {!isPendingCollapsed && (
              <>
                <div className="pending-contracts-actions">
                  <label className="select-all-label">
                    <input
                      type="checkbox"
                      checked={
                        selectedPendingContracts.size ===
                          pendingContracts.length && pendingContracts.length > 0
                      }
                      onChange={(e) => handleSelectAllPending(e.target.checked)}
                    />
                    <span>Sélectionner tous ({pendingContracts.length})</span>
                  </label>

                  {selectedPendingContracts.size > 0 && (
                    <>
                      <button
                        className="btn-send-contracts"
                        onClick={handleOpenSendConfirmModal}
                        disabled={sendingContracts}
                      >
                        Envoyer {selectedPendingContracts.size} contrat(s) pour
                        signature
                      </button>
                      <button
                        className="btn-cancel-contracts"
                        onClick={handleCancelPendingContracts}
                        disabled={sendingContracts}
                      >
                        Annuler {selectedPendingContracts.size} contrat(s)
                      </button>
                    </>
                  )}
                </div>

                <div className="pending-contracts-list">
                  {pendingContracts.map((contract) => {
                    const coding = contract.codages?.[0];
                    const coder = coding?.coder;
                    const enterprise = coder?.enterprise;
                    const game = coding?.game;

                    return (
                      <div key={contract.id} className="pending-contract-card">
                        <div className="pending-contract-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedPendingContracts.has(contract.id)}
                            onChange={(e) =>
                              handleSelectPendingContract(
                                contract.id,
                                e.target.checked,
                              )
                            }
                          />
                        </div>
                        <div className="pending-contract-info">
                          <div className="pending-contract-row">
                            <strong>Contrat #{contract.id}</strong>
                            <span className="pending-contract-type">
                              {coding?.aia_codage_type}
                              {coding?.aia_codage_task_type &&
                                ` · ${formatTaskType(
                                  coding.aia_codage_task_type,
                                )}`}
                            </span>
                          </div>
                          <div className="pending-contract-row">
                            <span>
                              {coder?.first_name} {coder?.last_name}
                            </span>
                            <span>{enterprise?.nom}</span>
                          </div>
                          <div className="pending-contract-row">
                            <span>{game?.gameName}</span>
                            <span>
                              {" "}
                              {game?.metadata?.match_date
                                ? new Date(
                                    game.metadata.match_date,
                                  ).toLocaleDateString()
                                : EMPTY_VALUE}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )} */}

        {/* Controls — the sync button has moved up beside the title, as on Codages.
            What is left here is the billing entry point and the view switch, both
            hidden; with neither shown the bar disappears rather than sitting empty. */}
        {(SHOW_BILLING || SHOW_KANBAN) && (
          <div className="page-controls">
            <div className="view-controls">
              {SHOW_BILLING && (
                <button
                  className="btn-primary"
                  onClick={handleOpenBillingModal}
                >
                  Facturer des contrats
                </button>
              )}
              {SHOW_KANBAN && (
                <>
                  <button
                    className={`view-btn ${viewMode === "table" ? "active" : ""}`}
                    onClick={() => setViewMode("table")}
                  >
                    Vue Tableau
                  </button>
                  <button
                    className={`view-btn ${viewMode === "kanban" ? "active" : ""}`}
                    onClick={() => setViewMode("kanban")}
                  >
                    Vue Kanban
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group filter-group-search">
              <label>Recherche</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
                placeholder="Nom, email ou entreprise..."
                className="filter-input"
              />
            </div>

            <div className="filter-group filter-group-contract-id">
              <label>ID Contrat</label>
              {/* Digits only: the API `parseInt`s this and drops the filter on NaN, so
                  letters would silently mean "no filter". Text input rather than
                  `type="number"`, to keep the spinner-free look of its neighbours. */}
              <input
                type="text"
                inputMode="numeric"
                value={filters.contractId}
                onChange={(e) =>
                  updateContractIdFilter(e.target.value.replace(/\D/g, ""))
                }
                placeholder="Numéro de contrat..."
                className="filter-input"
              />
            </div>

            <div className="filter-group filter-group-date">
              <label>Date de début</label>
              <input
                type="date"
                value={filters.dateStart}
                onChange={(e) => updateFilter("dateStart", e.target.value)}
                className="filter-input date-input"
              />
            </div>

            <div className="filter-group filter-group-date">
              <label>Date de fin</label>
              <input
                type="date"
                value={filters.dateEnd}
                onChange={(e) => updateFilter("dateEnd", e.target.value)}
                className="filter-input date-input"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>

          {/* The two multi-selects get a row to themselves: on one row with the rest
              they were narrow enough that the selected statuses wrapped onto a second
              line inside the control. */}
          <div className="filters-row">
            <div className="filter-group filter-group-status">
              <label>Statut du Contrat</label>
              <Select
                isMulti
                value={filters.contractStatus.map((status) => ({
                  value: status,
                  label: getContractStatusLabel(status),
                }))}
                onChange={(options) =>
                  updateFilter(
                    "contractStatus",
                    options ? options.map((opt: any) => opt.value) : [],
                  )
                }
                options={filterOptions.contractStatuses.map((status) => ({
                  value: status,
                  label: getContractStatusLabel(status),
                }))}
                styles={darkSelectStyles}
                placeholder="Sélectionner les statuts"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group filter-group-status">
              <label>Statut de Facturation</label>
              <Select
                isMulti
                value={filters.paymentStatus.map((status) => ({
                  value: status,
                  label:
                    status === "non facturé"
                      ? "Non Facturé"
                      : status === "facturé"
                        ? "Facturé"
                        : "Payé",
                }))}
                onChange={(options) =>
                  updateFilter(
                    "paymentStatus",
                    options ? options.map((opt: any) => opt.value) : [],
                  )
                }
                options={filterOptions.paymentStatuses.map((status) => ({
                  value: status,
                  label:
                    status === "non facturé"
                      ? "Non Facturé"
                      : status === "facturé"
                        ? "Facturé"
                        : "Payé",
                }))}
                styles={darkSelectStyles}
                placeholder="Sélectionner les statuts"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedContracts.size > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <input
                type="checkbox"
                checked={
                  contracts.length > 0 &&
                  selectedContracts.size === contracts.length
                }
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="bulk-checkbox"
              />
              <span className="selection-count">
                {selectedContracts.size} contrat
                {selectedContracts.size > 1 ? "s" : ""} sélectionné
                {selectedContracts.size > 1 ? "s" : ""}
              </span>
            </div>
            <div className="bulk-actions-buttons">
              {selectedPendingCount > 0 && (
                <>
                  <button
                    className="bulk-action-btn btn-send-contracts"
                    onClick={handleSendBulkPendingContracts}
                    disabled={sendingContracts}
                  >
                    {sendingContracts
                      ? "Envoi..."
                      : `Envoyer ${selectedPendingCount} contrat(s) pour signature`}
                  </button>
                  <button
                    className="bulk-action-btn btn-cancel-pending"
                    onClick={handleOpenBulkPendingCancelModal}
                  >
                    Annuler {selectedPendingCount} contrat(s) à traiter
                  </button>
                </>
              )}
              {selectedNonPendingCount > 0 && (
                <>
                  <button
                    className="bulk-action-btn btn-not-factured"
                    onClick={() => handleBulkUpdateStatus("non facturé")}
                  >
                    Marquer comme Non Facturé
                  </button>
                  <button
                    className="bulk-action-btn btn-factured"
                    onClick={() => handleBulkUpdateStatus("facturé")}
                  >
                    Marquer comme Facturé
                  </button>
                  <button
                    className="bulk-action-btn btn-paid"
                    onClick={() => handleBulkUpdateStatus("payé")}
                  >
                    Marquer comme Payé
                  </button>
                </>
              )}
              <button
                className="bulk-action-btn btn-cancel-contract"
                onClick={openBulkCancelModal}
              >
                Annuler les contrats
              </button>
              <button
                className="bulk-action-btn btn-cancel"
                onClick={() => setSelectedContracts(new Set())}
              >
                ✕ Désélectionner
              </button>
            </div>
          </div>
        )}

        <div className="page-content">
          {viewMode === "table" ? (
            <div className="contracts-table-container">
              <table className="contracts-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        checked={
                          contracts.length > 0 &&
                          selectedContracts.size === contracts.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="table-checkbox"
                      />
                    </th>
                    <th>ID Contrat</th>
                    <th>Type</th>
                    <th>Personne</th>
                    <th>Entreprise</th>
                    <th
                      className="sortable-header"
                      onClick={toggleDateSort}
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title="Cliquer pour changer l'ordre"
                    >
                      Date {dateSortOrder === "desc" ? "▼" : "▲"}
                    </th>
                    <th>Match</th>
                    <th>Statut Contrat</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedContracts.map((contract) => (
                    <React.Fragment key={contract.id}>
                      {/* Codings rows */}
                      {contract.codages.map((coding) => (
                        <tr key={`coding-${coding.id}`} className="coding-row">
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedContracts.has(contract.id)}
                              onChange={(e) =>
                                handleSelectContract(
                                  contract.id,
                                  e.target.checked,
                                )
                              }
                              className="table-checkbox"
                            />
                          </td>
                          <td>#{contract.id}</td>
                          <td>
                            {formatCodingTypeAndTask(
                              coding.aia_codage_type,
                              coding.aia_codage_task_type,
                            )}
                          </td>
                          <td>
                            <div className="person-info">
                              <span className="person-name">
                                {coding.coder.first_name}{" "}
                                {coding.coder.last_name}
                              </span>
                              <span className="person-role">Codeur</span>
                            </div>
                          </td>
                          <td>
                            <span className="enterprise-name">
                              {coding.coder.enterprise?.nom || EMPTY_VALUE}
                            </span>
                          </td>
                          <td>
                            {coding.game?.metadata?.match_date
                              ? new Date(
                                  coding.game.metadata.match_date,
                                ).toLocaleDateString()
                              : coding.game?.gameName
                                ? "Date inconnue"
                                : EMPTY_VALUE}
                          </td>
                          <td>
                            <span className="match-name">
                              {coding.aia_game_name}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-badge contract-${
                                contract.contractStatus || "unsigned"
                              }`}
                            >
                              {getContractStatusLabel(
                                contract.contractStatus || "unsigned",
                              )}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              {contract.contractStatus === "cancelled" ? (
                                <span
                                  className="cancelled-info"
                                  title={contract.cancellationReason}
                                >
                                  Annulé le{" "}
                                  {new Date(
                                    contract.cancelledAt!,
                                  ).toLocaleDateString()}
                                </span>
                              ) : contract.contractStatus === "no_contract" ? (
                                <>
                                  <span
                                    className="no-contract-info"
                                    title="Entreprise à facturation centralisée - Ne reçoit pas de contrats individuels"
                                  >
                                    Sans Contrat
                                  </span>
                                  <button
                                    className={`action-btn ${
                                      contract.status === "payé"
                                        ? "btn-paid"
                                        : contract.status === "facturé"
                                          ? "btn-factured"
                                          : "btn-not-factured"
                                    }`}
                                    onClick={() =>
                                      handleToggleFactureStatus(contract)
                                    }
                                    title={
                                      contract.status === "payé" &&
                                      (contract as any).paidAt
                                        ? `Payé le ${new Date(
                                            (contract as any).paidAt,
                                          ).toLocaleDateString()}`
                                        : ""
                                    }
                                  >
                                    {!contract.status ||
                                    contract.status === "non facturé"
                                      ? "Non Facturé"
                                      : contract.status === "facturé"
                                        ? "Facturé"
                                        : (contract as any).paidAt
                                          ? `✓ Payé le ${new Date(
                                              (contract as any).paidAt,
                                            ).toLocaleDateString()}`
                                          : "✓ Payé"}
                                  </button>
                                </>
                              ) : contract.contractStatus === "pending" ? (
                                <>
                                  <button
                                    className="action-btn btn-send-contract"
                                    onClick={() =>
                                      handleSendSingleContract(contract.id)
                                    }
                                    title="Envoyer ce contrat pour signature"
                                  >
                                    Envoyer
                                  </button>
                                  <button
                                    className="action-btn btn-cancel-single"
                                    onClick={() => {
                                      openCancelModal(contract.id);
                                    }}
                                    title="Annuler ce contrat"
                                  >
                                    Annuler
                                  </button>
                                </>
                              ) : AWAITING_SIGNATURE_STATUSES.includes(
                                  contract.contractStatus,
                                ) ? (
                                <>
                                  <button
                                    className="action-btn btn-resend-contract"
                                    onClick={() =>
                                      handleResendContract(contract.id)
                                    }
                                    title="Annuler la demande actuelle et renvoyer le contrat"
                                  >
                                    Renvoyer
                                  </button>
                                  <button
                                    className={`action-btn ${
                                      contract.status === "payé"
                                        ? "btn-paid"
                                        : contract.status === "facturé"
                                          ? "btn-factured"
                                          : "btn-not-factured"
                                    }`}
                                    onClick={() =>
                                      handleToggleFactureStatus(contract)
                                    }
                                    title={
                                      contract.status === "payé" &&
                                      (contract as any).paidAt
                                        ? `Payé le ${new Date(
                                            (contract as any).paidAt,
                                          ).toLocaleDateString()}`
                                        : ""
                                    }
                                  >
                                    {!contract.status ||
                                    contract.status === "non facturé"
                                      ? "Non Facturé"
                                      : contract.status === "facturé"
                                        ? "Facturé"
                                        : (contract as any).paidAt
                                          ? `✓ Payé le ${new Date(
                                              (contract as any).paidAt,
                                            ).toLocaleDateString()}`
                                          : "✓ Payé"}
                                  </button>
                                  <button
                                    className="action-btn btn-cancel-single"
                                    onClick={() => openCancelModal(contract.id)}
                                    title="Annuler ce contrat"
                                  >
                                    Annuler
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    className={`action-btn ${
                                      contract.status === "payé"
                                        ? "btn-paid"
                                        : contract.status === "facturé"
                                          ? "btn-factured"
                                          : "btn-not-factured"
                                    }`}
                                    onClick={() =>
                                      handleToggleFactureStatus(contract)
                                    }
                                    title={
                                      contract.status === "payé" &&
                                      (contract as any).paidAt
                                        ? `Payé le ${new Date(
                                            (contract as any).paidAt,
                                          ).toLocaleDateString()}`
                                        : ""
                                    }
                                  >
                                    {!contract.status ||
                                    contract.status === "non facturé"
                                      ? "Non Facturé"
                                      : contract.status === "facturé"
                                        ? "Facturé"
                                        : (contract as any).paidAt
                                          ? `✓ Payé le ${new Date(
                                              (contract as any).paidAt,
                                            ).toLocaleDateString()}`
                                          : "✓ Payé"}
                                  </button>
                                  <button
                                    className="action-btn btn-cancel-single"
                                    onClick={() => openCancelModal(contract.id)}
                                    title="Annuler ce contrat"
                                  >
                                    Annuler
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Supervisions rows */}
                      {contract.supervisions.map((supervision) => (
                        <tr
                          key={`supervision-${supervision.id}`}
                          className="supervision-row"
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedContracts.has(contract.id)}
                              onChange={(e) =>
                                handleSelectContract(
                                  contract.id,
                                  e.target.checked,
                                )
                              }
                              className="table-checkbox"
                            />
                          </td>
                          <td>#{contract.id}</td>
                          <td>
                            {formatCodingType(supervision.aia_supervision_type)}
                          </td>
                          <td>
                            <div className="person-info">
                              <span className="person-name">
                                {supervision.supervisor.first_name}{" "}
                                {supervision.supervisor.last_name}
                              </span>
                              <span className="person-role">Superviseur</span>
                            </div>
                          </td>
                          <td>
                            <span className="enterprise-name">
                              {supervision.supervisor.enterprise?.nom ||
                                EMPTY_VALUE}
                            </span>
                          </td>
                          <td>
                            {supervision.game?.metadata?.match_date
                              ? new Date(
                                  supervision.game.metadata.match_date,
                                ).toLocaleDateString()
                              : supervision.game?.gameName
                                ? "Date inconnue"
                                : EMPTY_VALUE}
                          </td>
                          <td>
                            <span className="match-name">
                              {supervision.aia_game_name}
                            </span>
                          </td>
                          <td>
                            <span
                              className={`status-badge contract-${
                                supervision.aia_supervision_type ===
                                "supervision"
                                  ? "not-required"
                                  : contract.contractStatus || "unsigned"
                              }`}
                            >
                              {supervision.aia_supervision_type ===
                              "supervision"
                                ? "Non requis"
                                : getContractStatusLabel(
                                    contract.contractStatus || "unsigned",
                                  )}
                            </span>
                          </td>
                          <td>
                            <div className="table-actions">
                              {supervision.aia_supervision_type ===
                              "supervision" ? (
                                <>
                                  {contract.contractStatus === "cancelled" ? (
                                    <span
                                      className="cancelled-info"
                                      title={contract.cancellationReason}
                                    >
                                      Annulé le{" "}
                                      {new Date(
                                        contract.cancelledAt!,
                                      ).toLocaleDateString()}
                                    </span>
                                  ) : contract.contractStatus ===
                                    "no_contract" ? (
                                    <>
                                      <span
                                        className="no-contract-info"
                                        title="Entreprise à facturation centralisée - Ne reçoit pas de contrats individuels"
                                      >
                                        Sans Contrat
                                      </span>
                                      <button
                                        className={`action-btn ${
                                          contract.status === "payé"
                                            ? "btn-paid"
                                            : contract.status === "facturé"
                                              ? "btn-factured"
                                              : "btn-not-factured"
                                        }`}
                                        onClick={() =>
                                          handleToggleFactureStatus(contract)
                                        }
                                        title={
                                          contract.status === "payé" &&
                                          (contract as any).paidAt
                                            ? `Payé le ${new Date(
                                                (contract as any).paidAt,
                                              ).toLocaleDateString()}`
                                            : ""
                                        }
                                      >
                                        {!contract.status ||
                                        contract.status === "non facturé"
                                          ? "Non Facturé"
                                          : contract.status === "facturé"
                                            ? "Facturé"
                                            : (contract as any).paidAt
                                              ? `✓ Payé le ${new Date(
                                                  (contract as any).paidAt,
                                                ).toLocaleDateString()}`
                                              : "✓ Payé"}
                                      </button>
                                    </>
                                  ) : contract.contractStatus === "pending" ? (
                                    <>
                                      <button
                                        className="action-btn btn-send-contract"
                                        onClick={() =>
                                          handleSendSingleContract(contract.id)
                                        }
                                        title="Envoyer ce contrat pour signature"
                                      >
                                        Envoyer
                                      </button>
                                      <button
                                        className="action-btn btn-cancel-single"
                                        onClick={() =>
                                          openCancelModal(contract.id)
                                        }
                                        title="Annuler ce contrat"
                                      >
                                        Annuler
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        className={`action-btn ${
                                          contract.status === "payé"
                                            ? "btn-paid"
                                            : contract.status === "facturé"
                                              ? "btn-factured"
                                              : "btn-not-factured"
                                        }`}
                                        onClick={() =>
                                          handleToggleFactureStatus(contract)
                                        }
                                        title={
                                          contract.status === "payé" &&
                                          (contract as any).paidAt
                                            ? `Payé le ${new Date(
                                                (contract as any).paidAt,
                                              ).toLocaleDateString()}`
                                            : ""
                                        }
                                      >
                                        {!contract.status ||
                                        contract.status === "non facturé"
                                          ? "Non Facturé"
                                          : contract.status === "facturé"
                                            ? "Facturé"
                                            : (contract as any).paidAt
                                              ? `✓ Payé le ${new Date(
                                                  (contract as any).paidAt,
                                                ).toLocaleDateString()}`
                                              : "✓ Payé"}
                                      </button>
                                      <button
                                        className="action-btn btn-cancel-single"
                                        onClick={() =>
                                          openCancelModal(contract.id)
                                        }
                                        title="Annuler ce contrat"
                                      >
                                        Annuler
                                      </button>
                                    </>
                                  )}
                                </>
                              ) : (
                                <>
                                  {contract.contractStatus === "cancelled" ? (
                                    <span
                                      className="cancelled-info"
                                      title={contract.cancellationReason}
                                    >
                                      Annulé le{" "}
                                      {new Date(
                                        contract.cancelledAt!,
                                      ).toLocaleDateString()}
                                    </span>
                                  ) : contract.contractStatus ===
                                    "no_contract" ? (
                                    <>
                                      <span
                                        className="no-contract-info"
                                        title="Entreprise à facturation centralisée - Ne reçoit pas de contrats individuels"
                                      >
                                        Sans Contrat
                                      </span>
                                      <button
                                        className={`action-btn ${
                                          contract.status === "payé"
                                            ? "btn-paid"
                                            : contract.status === "facturé"
                                              ? "btn-factured"
                                              : "btn-not-factured"
                                        }`}
                                        onClick={() =>
                                          handleToggleFactureStatus(contract)
                                        }
                                        title={
                                          contract.status === "payé" &&
                                          (contract as any).paidAt
                                            ? `Payé le ${new Date(
                                                (contract as any).paidAt,
                                              ).toLocaleDateString()}`
                                            : ""
                                        }
                                      >
                                        {!contract.status ||
                                        contract.status === "non facturé"
                                          ? "Non Facturé"
                                          : contract.status === "facturé"
                                            ? "Facturé"
                                            : (contract as any).paidAt
                                              ? `✓ Payé le ${new Date(
                                                  (contract as any).paidAt,
                                                ).toLocaleDateString()}`
                                              : "✓ Payé"}
                                      </button>
                                    </>
                                  ) : contract.contractStatus === "pending" ? (
                                    <>
                                      <button
                                        className="action-btn btn-send-contract"
                                        onClick={() =>
                                          handleSendSingleContract(contract.id)
                                        }
                                        title="Envoyer ce contrat pour signature"
                                      >
                                        Envoyer
                                      </button>
                                      <button
                                        className="action-btn btn-cancel-single"
                                        onClick={() =>
                                          openCancelModal(contract.id)
                                        }
                                        title="Annuler ce contrat"
                                      >
                                        Annuler
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        className="action-btn view-contract"
                                        onClick={() =>
                                          handleViewContract(contract)
                                        }
                                      >
                                        Voir Contrat
                                      </button>
                                      <button
                                        className={`action-btn ${
                                          contract.status === "payé"
                                            ? "btn-paid"
                                            : contract.status === "facturé"
                                              ? "btn-factured"
                                              : "btn-not-factured"
                                        }`}
                                        onClick={() =>
                                          handleToggleFactureStatus(contract)
                                        }
                                        title={
                                          contract.status === "payé" &&
                                          (contract as any).paidAt
                                            ? `Payé le ${new Date(
                                                (contract as any).paidAt,
                                              ).toLocaleDateString()}`
                                            : ""
                                        }
                                      >
                                        {!contract.status ||
                                        contract.status === "non facturé"
                                          ? "Non Facturé"
                                          : contract.status === "facturé"
                                            ? "Facturé"
                                            : (contract as any).paidAt
                                              ? `✓ Payé le ${new Date(
                                                  (contract as any).paidAt,
                                                ).toLocaleDateString()}`
                                              : "✓ Payé"}
                                      </button>
                                      <button
                                        className="action-btn btn-cancel-single"
                                        onClick={() =>
                                          openCancelModal(contract.id)
                                        }
                                        title="Annuler ce contrat"
                                      >
                                        Annuler
                                      </button>
                                    </>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="kanban-container">
              <div className="kanban-columns">
                {["no_contract", "unsigned", "signed", "cancelled"].map(
                  (status) => {
                    // Get all codings and supervisions for contracts with this status
                    const allItems = sortedContracts
                      .filter((contract) => contract.contractStatus === status)
                      .flatMap((contract) => [
                        ...contract.codages.map((coding) => ({
                          ...coding,
                          contractId: contract.id,
                          contractStatus: contract.contractStatus,
                          status: contract.status,
                          factureId: contract.factureId,
                          type: "coding",
                          fullContract: contract,
                        })),
                        ...contract.supervisions.map((supervision) => ({
                          ...supervision,
                          contractId: contract.id,
                          contractStatus: contract.contractStatus,
                          status: contract.status,
                          factureId: contract.factureId,
                          type: "supervision",
                          fullContract: contract,
                        })),
                      ]);

                    return (
                      <div key={status} className="kanban-column">
                        <div className="column-header">
                          <h3 className="column-title">
                            {status === "no_contract"
                              ? "Sans Contrat"
                              : status === "unsigned"
                                ? "Non Signé"
                                : status === "signed"
                                  ? "Signé"
                                  : status === "cancelled"
                                    ? "Annulé"
                                    : status.charAt(0).toUpperCase() +
                                      status.slice(1)}
                          </h3>
                          <span className="column-count">
                            {allItems.length}
                          </span>
                        </div>
                        <div className="column-content">
                          {allItems.map((item, index) => {
                            const isCoding = item.type === "coding";
                            const typeValue = isCoding
                              ? (item as any).aia_codage_type
                              : "supervision";
                            const personName = isCoding
                              ? `${(item as any).coder.first_name} ${
                                  (item as any).coder.last_name
                                }`
                              : `${(item as any).supervisor.first_name} ${
                                  (item as any).supervisor.last_name
                                }`;
                            const gameName = (item as any).aia_game_name;
                            const itemDate = (item as any).game?.metadata
                              ?.match_date
                              ? (item as any).game.metadata.match_date
                              : null;

                            return (
                              <div
                                key={`${item.type}-${item.id}`}
                                className={`kanban-card ${item.type}-card`}
                              >
                                <div className="card-header">
                                  <div className="card-title-row">
                                    <h4>Contrat #{item.contractId}</h4>
                                    <span className={`type-badge ${typeValue}`}>
                                      {typeValue}
                                    </span>
                                  </div>
                                </div>
                                <div className="card-content">
                                  <div className="person-info">
                                    <span className="person-name">
                                      {personName}
                                    </span>
                                    <span className="person-role">
                                      {isCoding ? "Codeur" : "Superviseur"}
                                    </span>
                                  </div>
                                  <div className="item-details">
                                    <div className="match-info">
                                      <span className="match-name">
                                        {gameName}
                                      </span>
                                    </div>
                                    <div className="date-info">
                                      <span className="item-date">
                                        {itemDate
                                          ? new Date(itemDate)
                                              .toISOString()
                                              .split("T")[0]
                                          : "Date inconnue"}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="status-badges">
                                    <span
                                      className={`status-badge contract-${
                                        item.contractStatus || "unsigned"
                                      }`}
                                    >
                                      {getContractStatusLabel(
                                        item.contractStatus || "unsigned",
                                      )}
                                    </span>
                                    <span
                                      className={`status-badge payment-${
                                        (item as any).status || "non facturé"
                                      }`}
                                    >
                                      {!(item as any).status ||
                                      (item as any).status === "non facturé"
                                        ? "Non Facturé"
                                        : (item as any).status === "facturé"
                                          ? "Facturé"
                                          : "Payé"}
                                    </span>
                                  </div>
                                </div>
                                <div className="card-actions">
                                  {item.contractStatus === "cancelled" ? (
                                    <span
                                      className="cancelled-info"
                                      title={
                                        (item as any).fullContract
                                          .cancellationReason
                                      }
                                    >
                                      Annulé le{" "}
                                      {new Date(
                                        (item as any).fullContract.cancelledAt!,
                                      ).toLocaleDateString()}
                                    </span>
                                  ) : item.contractStatus === "no_contract" ? (
                                    <>
                                      <span
                                        className="no-contract-info"
                                        title="Entreprise à facturation centralisée - Ne reçoit pas de contrats individuels"
                                      >
                                        Sans Contrat
                                      </span>
                                      <button
                                        className={`action-btn ${
                                          (item as any).status === "payé"
                                            ? "btn-paid"
                                            : (item as any).status === "facturé"
                                              ? "btn-factured"
                                              : "btn-not-factured"
                                        }`}
                                        onClick={() =>
                                          handleToggleFactureStatus(
                                            (item as any).fullContract,
                                          )
                                        }
                                        title={
                                          (item as any).status === "payé" &&
                                          (item as any).fullContract.paidAt
                                            ? `Payé le ${new Date(
                                                (item as any).fullContract
                                                  .paidAt,
                                              ).toLocaleDateString()}`
                                            : ""
                                        }
                                      >
                                        {!(item as any).status ||
                                        (item as any).status === "non facturé"
                                          ? "Non Facturé"
                                          : (item as any).status === "facturé"
                                            ? "Facturé"
                                            : (item as any).fullContract.paidAt
                                              ? `✓ Payé le ${new Date(
                                                  (item as any).fullContract
                                                    .paidAt,
                                                ).toLocaleDateString()}`
                                              : "✓ Payé"}
                                      </button>
                                      <button
                                        className="action-btn btn-cancel-single"
                                        onClick={() =>
                                          openCancelModal(
                                            (item as any).fullContract.id,
                                          )
                                        }
                                        title="Annuler ce contrat"
                                      ></button>
                                    </>
                                  ) : (
                                    <>
                                      {!isCoding &&
                                      (item as any).aia_supervision_type ===
                                        "supervision" ? (
                                        <>
                                          <button
                                            className={`action-btn ${
                                              (item as any).status === "payé"
                                                ? "btn-paid"
                                                : (item as any).status ===
                                                    "facturé"
                                                  ? "btn-factured"
                                                  : "btn-not-factured"
                                            }`}
                                            onClick={() =>
                                              handleToggleFactureStatus(
                                                (item as any).fullContract,
                                              )
                                            }
                                            title={
                                              (item as any).status === "payé" &&
                                              (item as any).fullContract.paidAt
                                                ? `Payé le ${new Date(
                                                    (item as any).fullContract
                                                      .paidAt,
                                                  ).toLocaleDateString()}`
                                                : ""
                                            }
                                          >
                                            {!(item as any).status ||
                                            (item as any).status ===
                                              "non facturé"
                                              ? "Non Facturé"
                                              : (item as any).status ===
                                                  "facturé"
                                                ? "Facturé"
                                                : (item as any).fullContract
                                                      .paidAt
                                                  ? `✓ Payé le ${new Date(
                                                      (item as any).fullContract
                                                        .paidAt,
                                                    ).toLocaleDateString()}`
                                                  : "✓ Payé"}
                                          </button>
                                          <button
                                            className="action-btn btn-cancel-single"
                                            onClick={() =>
                                              openCancelModal(
                                                (item as any).fullContract.id,
                                              )
                                            }
                                            title="Annuler ce contrat"
                                          ></button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            className="action-btn view-contract"
                                            onClick={() =>
                                              handleViewContract(
                                                (item as any).fullContract,
                                              )
                                            }
                                          >
                                            Voir Contrat
                                          </button>
                                          <button
                                            className={`action-btn ${
                                              (item as any).status === "payé"
                                                ? "btn-paid"
                                                : (item as any).status ===
                                                    "facturé"
                                                  ? "btn-factured"
                                                  : "btn-not-factured"
                                            }`}
                                            onClick={() =>
                                              handleToggleFactureStatus(
                                                (item as any).fullContract,
                                              )
                                            }
                                            title={
                                              (item as any).status === "payé" &&
                                              (item as any).fullContract.paidAt
                                                ? `Payé le ${new Date(
                                                    (item as any).fullContract
                                                      .paidAt,
                                                  ).toLocaleDateString()}`
                                                : ""
                                            }
                                          >
                                            {!(item as any).status ||
                                            (item as any).status ===
                                              "non facturé"
                                              ? "Non Facturé"
                                              : (item as any).status ===
                                                  "facturé"
                                                ? "Facturé"
                                                : (item as any).fullContract
                                                      .paidAt
                                                  ? `✓ Payé le ${new Date(
                                                      (item as any).fullContract
                                                        .paidAt,
                                                    ).toLocaleDateString()}`
                                                  : "✓ Payé"}
                                          </button>
                                          <button
                                            className="action-btn btn-cancel-single"
                                            onClick={() =>
                                              openCancelModal(
                                                (item as any).fullContract.id,
                                              )
                                            }
                                            title="Annuler ce contrat"
                                          ></button>
                                        </>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Page {currentPage} sur {totalPages} ({totalItems} contrats au
                total)
              </span>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
            <div className="page-size-selector">
              <label>Éléments par page</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="page-size-select"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Contract Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>
                {isBulkCancel
                  ? `Annuler ${selectedContracts.size} contrat${
                      selectedContracts.size > 1 ? "s" : ""
                    }`
                  : "Annuler le contrat"}
              </h3>
              <button className="close-btn" onClick={closeCancelModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning">
                Cette action marquera le contrat comme annulé et empêchera son
                envoi pour signature. Cette action est définitive.
                <br />
                <br />
                Si le contrat a été envoyé pour signature, la demande sera
                automatiquement rappelée.
              </div>
              <div className="form-section">
                <label className="form-label">
                  Motif d'annulation <span className="required">*</span>
                </label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Veuillez indiquer la raison de l'annulation..."
                  className="form-textarea"
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeCancelModal}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleCancelContract}
                disabled={!cancellationReason.trim()}
              >
                Confirmer l'annulation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && (
        <div className="modal-overlay">
          <div className="modal-content billing-modal">
            <div className="modal-header">
              <h2>Facturation des Contrats</h2>
              <button
                className="close-btn"
                onClick={() => setShowBillingModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Entreprise</label>
                <Select
                  options={enterprises.map((ent) => ({
                    value: ent.id,
                    label: ent.nom,
                  }))}
                  value={
                    selectedEnterpriseForBilling
                      ? {
                          value: selectedEnterpriseForBilling.id,
                          label: selectedEnterpriseForBilling.nom,
                        }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleEnterpriseSelectForBilling(
                      selectedOption
                        ? {
                            id: selectedOption.value,
                            nom: selectedOption.label,
                          }
                        : null,
                    )
                  }
                  styles={formSelectStyles}
                  placeholder="Sélectionner une entreprise..."
                  isClearable
                  menuPosition="fixed"
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                />
              </div>

              {selectedEnterpriseForBilling && (
                <div className="contracts-section">
                  <div className="contracts-summary-boxes">
                    <div className="summary-box">
                      <div className="summary-box-header">Indiv</div>
                      <div className="summary-box-count">
                        {contractsByType.Individual.selected}/
                        {contractsByType.Individual.total}
                      </div>
                    </div>
                    <div className="summary-box">
                      <div className="summary-box-header">Collectif</div>
                      <div className="summary-box-count">
                        {contractsByType.Collective.selected}/
                        {contractsByType.Collective.total}
                      </div>
                    </div>
                    <div className="summary-box">
                      <div className="summary-box-header">Live</div>
                      <div className="summary-box-count">
                        {contractsByType.Live.selected}/
                        {contractsByType.Live.total}
                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="billing-filters">
                    <div className="filter-row">
                      <div className="filter-group">
                        <label>Date début</label>
                        <input
                          type="date"
                          value={billingFilters.dateStart}
                          onChange={(e) =>
                            setBillingFilters({
                              ...billingFilters,
                              dateStart: e.target.value,
                            })
                          }
                          className="filter-input"
                        />
                      </div>
                      <div className="filter-group">
                        <label>Date fin</label>
                        <input
                          type="date"
                          value={billingFilters.dateEnd}
                          onChange={(e) =>
                            setBillingFilters({
                              ...billingFilters,
                              dateEnd: e.target.value,
                            })
                          }
                          className="filter-input"
                        />
                      </div>
                      <div className="filter-group">
                        <label>Statut (Filtre)</label>
                        <select
                          value={billingFilters.status}
                          onChange={(e) =>
                            setBillingFilters({
                              ...billingFilters,
                              status: e.target.value,
                            })
                          }
                          className="filter-input"
                        >
                          <option value="">Tous</option>
                          <option value="non facturé">Non facturé</option>
                          <option value="facturé">Facturé</option>
                          <option value="payé">Payé</option>
                        </select>
                      </div>
                    </div>
                    <div className="filter-row" style={{ marginTop: "15px" }}>
                      <div className="filter-group">
                        <label>Statut à appliquer</label>
                        <select
                          value={statusToApply}
                          onChange={(e) =>
                            setStatusToApply(
                              e.target.value as "facturé" | "payé",
                            )
                          }
                          className="filter-input"
                          style={{
                            background: "rgba(25, 217, 22, 0.1)",
                            border: "2px solid rgba(25, 217, 22, 0.3)",
                            fontWeight: "600",
                          }}
                        >
                          <option value="facturé">Facturé</option>
                          <option value="payé">Payé</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <h3 style={{ marginTop: "20px", marginBottom: "15px" }}>
                    Contrats
                  </h3>

                  <div className="contracts-list">
                    {getFilteredBillingContracts().length === 0 ? (
                      <div className="no-contracts">
                        {unbilledContracts.length === 0
                          ? "Aucun contrat pour cette entreprise"
                          : "Aucun contrat ne correspond aux filtres"}
                      </div>
                    ) : (
                      getFilteredBillingContracts().map((contract) => {
                        const codingType =
                          contract.codages?.[0]?.aia_codage_type || "Unknown";
                        const isSelected = selectedContractIds.has(contract.id);
                        const isBilled = contract.status === "facturé";
                        const isPaid = contract.status === "payé";
                        const coderName = contract.codages?.[0]?.coder
                          ? `${contract.codages[0].coder.first_name} ${contract.codages[0].coder.last_name}`
                          : EMPTY_VALUE;
                        const gameName =
                          contract.codages?.[0]?.aia_game_name || EMPTY_VALUE;
                        const gameDate = contract.codages?.[0]?.aia_codage_Date
                          ? new Date(
                              contract.codages[0].aia_codage_Date,
                            ).toLocaleDateString()
                          : EMPTY_VALUE;

                        return (
                          <div
                            key={contract.id}
                            className={`contract-item ${
                              isSelected ? "selected" : ""
                            } ${
                              isPaid ? "paid" : isBilled ? "billed" : "unbilled"
                            }`}
                          >
                            <div className="contract-item-content">
                              <div className="contract-item-label">Contrat</div>
                              <div className="contract-item-details">
                                <div className="contract-detail-row">
                                  <strong>ID:</strong> #{contract.id}
                                </div>
                                <div className="contract-detail-row">
                                  <strong>Type:</strong> {codingType}
                                </div>
                                <div className="contract-detail-row">
                                  <strong>Codeur:</strong> {coderName}
                                </div>
                                <div className="contract-detail-row">
                                  <strong>Match:</strong> {gameName}
                                </div>
                                <div className="contract-detail-row">
                                  <strong>Date:</strong> {gameDate}
                                </div>
                                <div className="contract-detail-row">
                                  <strong>Statut:</strong>
                                  <span
                                    className={`status-label ${
                                      isPaid
                                        ? "paid"
                                        : isBilled
                                          ? "billed"
                                          : "unbilled"
                                    }`}
                                  >
                                    {isPaid
                                      ? "Payé"
                                      : isBilled
                                        ? "Facturé"
                                        : "Non facturé"}
                                  </span>
                                </div>
                                {isPaid && (contract as any).paidAt && (
                                  <div className="contract-detail-row">
                                    <strong>Payé le:</strong>{" "}
                                    {new Date(
                                      (contract as any).paidAt,
                                    ).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                            {!isPaid && (
                              <button
                                className="contract-add-btn"
                                onClick={() =>
                                  handleToggleContractSelection(
                                    contract.id,
                                    codingType,
                                  )
                                }
                              >
                                {isSelected ? "−" : "+"}
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowBillingModal(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleValidateBilling}
              >
                Valider la Facturation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Contracts Confirmation Modal */}
      {showSendConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirmer l'envoi des contrats</h3>
              <button
                className="close-btn"
                onClick={() => setShowSendConfirmModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="alert alert-info">
                Vous êtes sur le point d'envoyer{" "}
                <strong>{selectedPendingContracts.size} contrat(s)</strong> pour
                signature via AIA Sign.
                <br />
                <br />
                Les contrats seront envoyés par email aux entreprises
                concernées. Cette action est irréversible.
              </div>

              <div className="contracts-to-send-list">
                <h4>Contrats sélectionnés :</h4>
                <ul>
                  {Array.from(selectedPendingContracts).map((contractId) => {
                    const contract = pendingContracts.find(
                      (c) => c.id === contractId,
                    );
                    if (!contract) return null;

                    const coding = contract.codages?.[0];
                    const coder = coding?.coder;
                    const enterprise = coder?.enterprise;

                    return (
                      <li key={contractId}>
                        <strong>Contrat #{contractId}</strong> -{" "}
                        {coder?.first_name} {coder?.last_name} (
                        {enterprise?.nom})
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowSendConfirmModal(false)}
                disabled={sendingContracts}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSendPendingContracts}
                disabled={sendingContracts}
              >
                {sendingContracts ? "Envoi en cours..." : "Confirmer l'envoi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send Contract for Signature Modal - REMOVED */}
    </div>
  );
};

export default Contracts;
